// components/layout/Sidebar.jsx
import React, { useState } from "react";
import {
  Menu,
  X,
  Home,
  FileText,
  BookOpen,
  UserCheck,
  BarChart2,
  HelpCircle,
  Settings,
  Users,
  Layers,
  ClipboardList,
  CheckSquare,
  Building,
  Building2,
  Headphones,
  Book,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSession } from "../../session/SessionProvider";
import { CDN_BASE_URL } from "../../config";

// --- helpers ---
const keysFromPermissions = (permissions) =>
  Array.isArray(permissions)
    ? permissions
        .map((p) => (typeof p === "string" ? p : p?.key))
        .filter(Boolean)
    : [];

// اولویت با permissions (سرور). اگر نبود → perms
const buildEffectivePerms = (u) => {
  const fromServer = keysFromPermissions(u?.permissions);
  if (fromServer.length) return Array.from(new Set(fromServer));
  const fromLocal = Array.isArray(u?.perms) ? u.perms : [];
  return Array.from(new Set(fromLocal));
};

const matchWithWildcard = (perms, key) => {
  if (!Array.isArray(perms)) return false;

  if (perms.includes("*") || perms.includes("platform:*")) return true;
  if (perms.includes(key)) return true;
  const parts = key.split(":");
  for (let i = parts.length; i >= 1; i--) {
    const cand = parts.slice(0, i).join(":") + ":*";
    if (perms.includes(cand)) return true;
  }
  return false;
};
const hasPermFactory = (perms) => (key) => matchWithWildcard(perms, key);
const isOrgUser = (perms) =>
  (perms || []).some(
    (k) =>
      typeof k === "string" &&
      (k.startsWith("org:") || k.startsWith("platform:"))
  );

// --- static menus ---
const getGroupedUserMenu = (t) => [
  {
    group: t("sidebar.dashboard_group"),
    items: [
      {
        label: t("sidebar.dashboard_user"),
        href: "/dashboard",
        icon: <Home size={20} />,
      },
    ],
  },
  {
    group: t("sidebar.subscription_group"),
    items: [
      {
        label: t("sidebar.subscriptions"),
        href: "/subscription",
        icon: <UserCheck size={20} />,
      },
    ],
  },
  {
    group: t("sidebar.exams_group"),
    items: [
      {
        label: t("sidebar.my_exams"),
        href: "/exams",
        icon: <FileText size={20} />,
      },
      {
        label: t("sidebar.exam_results"),
        href: "/result",
        icon: <BarChart2 size={20} />,
      },
    ],
  },
  {
    group: t("sidebar.education_group"),
    items: [
      {
        label: t("sidebar.educational_resources"),
        href: "/educationalresource",
        icon: <BookOpen size={20} />,
      },
    ],
  },
  {
    group: t("sidebar.general_group"),
    items: [
      {
        label: t("sidebar.support"),
        href: "/support",
        icon: <HelpCircle size={20} />,
      },
      {
        label: t("sidebar.account_settings"),
        href: "/settings",
        icon: <Settings size={20} />,
      },
    ],
  },
];

const getGroupedOrganizationMenu = (t) => [
  {
    group: t("sidebar.org_panel"),
    items: [
      {
        label: t("sidebar.dashboard_org"),
        href: "/organization",
        icon: <Home size={20} />,
      },
    ],
  },
  {
    group: t("sidebar.org_management"),
    items: [
      {
        label: t("sidebar.manage_users"),
        href: "/organization/users",
        icon: <Users size={20} />,
        perm: "org:users:read",
      },
      {
        label: t("sidebar.manage_organizations"),
        href: "/organization/organizations",
        icon: <Building size={20} />,
        perm: "org:organization:read",
      },
      {
        label: t("sidebar.exam_categories"),
        href: "/organization/categories",
        icon: <Layers size={20} />,
        perm: "org:categories:read",
      },
      {
        label: t("sidebar.support"),
        href: "/organization/support",
        icon: <Headphones size={20} />,
        perm: "org:support:read",
      },
      {
        label: t("sidebar.educational_resources"),
        href: "/organization/resource",
        icon: <Book size={20} />,
        perm: "org:resource:read",
      },
    ],
  },
  {
    group: t("sidebar.exams_group"),
    items: [
      {
        label: t("sidebar.exams"),
        href: "/organization/exams",
        icon: <FileText size={20} />,
        perm: "org:exams:read",
      },
      {
        label: t("sidebar.exam_results_org"),
        href: "/organization/results",
        icon: <ClipboardList size={20} />,
        perm: "org:results:read",
      },
      {
        label: t("sidebar.grading_queue"),
        href: "/organization/grading",
        icon: <CheckSquare size={20} />,
        perm: "org:grading:read",
      },
    ],
  },
];

const Sidebar = () => {
  const { t, i18n } = useTranslation();
  const { user, loading } = useSession(); // ← از Provider

  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isRtl = i18n.language.includes("fa");

  if (loading) {
    return (
      <aside
        className={`fixed top-0 h-full w-64 bg-white z-50 ${
          isRtl ? "right-0" : "left-0"
        } p-4`}
      >
        <div className="animate-pulse space-y-3">
          <div className="h-8 bg-gray-200 rounded" />
          <div className="h-6 bg-gray-200 rounded" />
          <div className="h-6 bg-gray-200 rounded" />
        </div>
      </aside>
    );
  }

  const perms = buildEffectivePerms(user);
  const hasPerm = hasPermFactory(perms);
  const orgUser = isOrgUser(perms);

  const menuItems = orgUser
    ? getGroupedOrganizationMenu(t)
    : getGroupedUserMenu(t);

  const sidebarClass = `fixed top-0 h-full w-64 bg-white transition-transform z-50 transform
    ${isRtl ? "right-0" : "left-0"}
    ${open ? "translate-x-0" : isRtl ? "translate-x-full" : "-translate-x-full"}
    md:translate-x-0 md:block`;

  const toggleButtonClass = `md:hidden p-2 fixed top-4 z-40 rounded-full bg-purple-100 hover:bg-purple-200
    ${isRtl ? "right-3" : "left-3"}`;
  const closeButtonContainerClass = `md:hidden p-4 flex justify-end`;
  const logoContainerClass = `flex items-center gap-2 px-6 py-4 ${
    isRtl ? "flex-row" : "flex-row-reverse"
  }`;

  return (
    <>
      <button className={toggleButtonClass} onClick={() => setOpen(true)}>
        <Menu className="w-5 h-5 text-purple-700" />
      </button>

      <aside className={sidebarClass}>
        <div className={closeButtonContainerClass}>
          <button onClick={() => setOpen(false)}>
            <X />
          </button>
        </div>

        <div className={logoContainerClass}>
          {user.organization?.name ? (
            <div>
              <img
                src={CDN_BASE_URL + user.organization.logo}
                className="w-8 h-8"
                alt={`${user.organization?.name} Logo`}
              />
            </div>
          ) : (
            <div>
              <Building2 />
            </div>
          )}

          <span className="font-bold text-xl text-gray-800">
            {user.organization?.name
              ? user.organization?.name
              : t("sidebar.user_panel_title")}
          </span>
        </div>

        <nav className="px-4 flex flex-col gap-2 text-sm">
          {menuItems.map((group, idx) => {
            const visible = group.items.filter(
              (it) => !it.perm || hasPerm(it.perm)
            );
            if (visible.length === 0) return null;
            return (
              <div key={idx}>
                <p className="text-sm text-gray-900 font-bold px-3 mb-2 mt-3">
                  {group.group}
                </p>
                <div className="px-1">
                  {visible.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`flex items-center gap-2 px-4 py-3 rounded-full transition
                        ${
                          location.pathname === item.href
                            ? "bg-purple-100 text-purple-700 font-semibold"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
      </aside>

      {open && (
        <div
          className="fixed inset-0 bg-black/30 z-40 md:hidden"
          onClick={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
